import { useCallback, useMemo } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  CalendarDays,
  FileEdit,
  Inbox,
  Mail,
  MessageSquare,
  RefreshCw,
  Send,
} from 'lucide-react';

import { Button } from '../../ui/button';
import { Card, CardContent } from '../../ui/card';
import CockpitHome from '../CockpitHome';
import { CockpitPanel, CockpitTabs, CockpitWorkspaceContent } from '../cockpit';
import { adminRoutes } from '../navigation/adminRoutes';
import {
  getRememberedCockpitPath,
  getRememberedSectionPath,
} from '../navigation/adminPlaceMemory';
import { canAccessSection, getAdminUser } from '../navigation/sectionAccess';

import { useOutreachCockpitData } from './useOutreachCockpitData';

const ERROR_LABELS = {
  unreadInbox: 'inbox',
  subscribers: 'subscriber count',
  newsletters: 'newsletter history',
  scheduledPosts: 'scheduled posts',
  approvedPosts: 'approved posts',
  events: 'events',
  feedback: 'feedback',
};

const formatCount = (value) => value?.toLocaleString?.() ?? String(value ?? 0);

const formatDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const newestBy = (items, field) =>
  [...(items || [])]
    .filter((item) => item?.[field])
    .sort((a, b) => new Date(b[field]).getTime() - new Date(a[field]).getTime())[0] || null;

const oldestBy = (items, field) =>
  [...(items || [])]
    .filter((item) => item?.[field])
    .sort((a, b) => new Date(a[field]).getTime() - new Date(b[field]).getTime())[0] || null;

const metricValue = (value, loading, failed) => {
  if (failed) return '—';
  if (loading && value == null) return '…';
  return formatCount(value);
};

export default function OutreachCockpitHome() {
  const location = useLocation();
  const navigate = useNavigate();
  const adminUser = useMemo(() => getAdminUser(), []);
  const hasAccess = useCallback(
    (sectionId) => canAccessSection(sectionId, adminUser),
    [adminUser]
  );
  const sections = useMemo(
    () => adminRoutes.outreach.sections.filter((section) => hasAccess(section.id)),
    [hasAccess]
  );
  const normalizedPath = location.pathname.replace(/\/+$/, '') || '/';
  // Prefix match keeps the section tab active on nested legacy routes such as
  // /admin/outreach/social/new or /admin/outreach/social/:postId.
  const activeSection = sections.find(
    (section) =>
      section.path === normalizedPath || normalizedPath.startsWith(`${section.path}/`)
  );

  const { data, errors, loading, refresh } = useOutreachCockpitData();
  const failed = useMemo(() => new Set(errors), [errors]);

  const latestNewsletter = newestBy(data.newsletters, 'sent_at');
  const nextEvent = oldestBy(data.events, 'date');
  const scheduledCount = data.scheduledPosts?.total;
  const approvedCount = data.approvedPosts?.total;
  const socialCount = scheduledCount == null || approvedCount == null
    ? null
    : scheduledCount + approvedCount;

  const kpis = [
    {
      id: 'unread-inbox',
      title: 'Unread inbox',
      value: metricValue(data.unreadInbox?.count, loading, failed.has('unreadInbox')),
      subtitle: data.unreadInbox?.count ? 'Needs attention' : 'Inbox is clear',
      icon: Inbox,
      tone: data.unreadInbox?.count ? 'danger' : undefined,
    },
    {
      id: 'newsletter-subscribers',
      title: 'Subscribers',
      value: metricValue(data.subscribers?.count, loading, failed.has('subscribers')),
      subtitle: latestNewsletter
        ? `Last send ${formatDate(latestNewsletter.sent_at)}`
        : failed.has('newsletters') ? 'Send history unavailable' : 'No sends yet',
      icon: Mail,
    },
    {
      id: 'queued-social',
      title: 'Queued social posts',
      value: metricValue(
        socialCount,
        loading,
        failed.has('scheduledPosts') || failed.has('approvedPosts')
      ),
      subtitle: scheduledCount != null && approvedCount != null
        ? `${formatCount(scheduledCount)} scheduled · ${formatCount(approvedCount)} approved`
        : 'Queue unavailable',
      icon: Send,
    },
    {
      id: 'upcoming-events',
      title: 'Upcoming events',
      value: metricValue(data.events?.length, loading, failed.has('events')),
      subtitle: nextEvent
        ? `Next: ${nextEvent.title} · ${formatDate(nextEvent.date)}`
        : failed.has('events') ? 'Events unavailable' : 'Nothing scheduled',
      icon: CalendarDays,
    },
    {
      id: 'recent-feedback',
      title: 'Feedback (30 days)',
      value: metricValue(data.feedback?.total_submissions, loading, failed.has('feedback')),
      subtitle: data.feedback
        ? `NPS ${data.feedback.nps_score} · ${data.feedback.average_rating}/5 average`
        : failed.has('feedback') ? 'Feedback unavailable' : 'Loading summary',
      icon: MessageSquare,
    },
  ];

  const quickActions = [
    {
      id: 'open-inbox',
      label: data.unreadInbox?.count
        ? `Open inbox (${formatCount(data.unreadInbox.count)})`
        : 'Open inbox',
      icon: Inbox,
      path: '/admin/outreach/inbox',
      sectionId: 'inbox',
    },
    {
      id: 'compose-newsletter',
      label: 'Compose newsletter',
      icon: FileEdit,
      path: '/admin/outreach/newsletter?tab=newsletters&compose=1',
      sectionId: 'newsletter',
    },
    {
      id: 'schedule-post',
      label: 'Schedule post',
      icon: Send,
      path: '/admin/outreach/social/new',
      sectionId: 'socialmedia',
    },
  ];

  if (!activeSection) {
    return sections[0]
      ? <Navigate to={getRememberedCockpitPath('outreach', sections, adminUser)} replace />
      : <Navigate to="/admin" replace />;
  }

  const panelId = `outreach-cockpit-tab-panel-${activeSection.id}`;

  return (
    <CockpitHome
      categoryId="outreach"
      description="Messages, campaigns, social publishing, events, and customer sentiment."
      kpis={kpis}
      quickActions={quickActions}
      canAccessSection={hasAccess}
      showSectionLinks={false}
      alerts={errors.length > 0 && (
        <Card className="border-amber-300 bg-amber-50/60 dark:border-amber-800 dark:bg-amber-950/20">
          <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div aria-live="polite">
              <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
                Some live metrics could not be loaded
              </p>
              <p className="text-xs text-amber-800 dark:text-amber-300">
                Unavailable: {errors.map((key) => ERROR_LABELS[key]).join(', ')}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={refresh} disabled={loading}>
              <RefreshCw className={loading ? 'animate-spin' : ''} />
              Retry
            </Button>
          </CardContent>
        </Card>
      )}
    >
      <CockpitTabs
        tabs={sections}
        active={activeSection.id}
        onChange={(sectionId) => {
          const section = sections.find((candidate) => candidate.id === sectionId);
          if (section) navigate(getRememberedSectionPath(section, adminUser));
        }}
        label="Outreach and communications sections"
        idPrefix="outreach-cockpit-tab"
      />

      <CockpitPanel
        id={panelId}
        aria-labelledby={`outreach-cockpit-tab-${activeSection.id}`}
      >
        <CockpitWorkspaceContent>
          <Outlet context={{ activeSection, activeSubsection: null }} />
        </CockpitWorkspaceContent>
      </CockpitPanel>
    </CockpitHome>
  );
}
